import React, { useMemo } from "react";

import { EmptyState, SectionCard } from "components/admin";
import { SchoolPlayerProfileTabProps } from "pages/app/workspace/school-player-profile/school-player-profile.types";
import {
  SchoolPlayerProfileEmptyCopy,
  SchoolPlayerProfileEyebrowText,
  SchoolPlayerProfilePerformanceAchievementMeta,
  SchoolPlayerProfilePerformanceAchievementRow,
  SchoolPlayerProfilePerformanceAchievementTitle,
  SchoolPlayerProfilePerformanceBarFill,
  SchoolPlayerProfilePerformanceBarTrack,
  SchoolPlayerProfilePerformanceCareerMeta,
  SchoolPlayerProfilePerformanceCareerRow,
  SchoolPlayerProfilePerformanceCareerTeam,
  SchoolPlayerProfilePerformanceCareerYear,
  SchoolPlayerProfilePerformanceList,
  SchoolPlayerProfilePerformanceMetricGrid,
  SchoolPlayerProfilePerformanceMetricTile,
  SchoolPlayerProfilePerformanceMetricValue,
  SchoolPlayerProfilePerformancePositionHeader,
  SchoolPlayerProfilePerformancePositionMeta,
  SchoolPlayerProfilePerformancePositionName,
  SchoolPlayerProfilePerformancePositionRow,
  SchoolPlayerProfilePerformancePrimaryGrid,
  SchoolPlayerProfilePerformanceSeasonHeader,
  SchoolPlayerProfilePerformanceSeasonMeta,
  SchoolPlayerProfilePerformanceSeasonRow,
  SchoolPlayerProfilePerformanceSeasonTitle,
  SchoolPlayerProfilePerformanceSecondaryGrid,
  SchoolPlayerProfilePerformanceSnapshot,
  SchoolPlayerProfilePerformanceSnapshotTitle,
  SchoolPlayerProfileTabStack,
} from "pages/app/workspace/styles/school-player-profile.page.inline.styles";

const asNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const seasonSortKey = (value: unknown) => String(value || "").toLowerCase();

const appearanceLabel = (count: number) =>
  `${count} ${count === 1 ? "appearance" : "appearances"}`;

export const SchoolPlayerPerformanceTab: React.FC<SchoolPlayerProfileTabProps> = ({
  athlete,
  athleteLoading,
}) => {
  const careers = useMemo(() => {
    const rows = [...(athlete?.player_careers || [])];
    rows.sort((left: any, right: any) => asNumber(right.start_year) - asNumber(left.start_year));
    return rows;
  }, [athlete?.player_careers]);

  const seasonStats = useMemo(() => {
    const rows = [...(athlete?.player_season_stats || [])];
    rows.sort((left: any, right: any) =>
      seasonSortKey(right.season).localeCompare(seasonSortKey(left.season)),
    );
    return rows;
  }, [athlete?.player_season_stats]);

  const positions = useMemo(() => {
    const rows = [...(athlete?.player_positions_played || [])];
    rows.sort((left: any, right: any) => asNumber(right.percentage) - asNumber(left.percentage));
    return rows;
  }, [athlete?.player_positions_played]);

  const achievements = useMemo(() => {
    const rows = [...(athlete?.achievements || [])];
    rows.sort(
      (left: any, right: any) =>
        asNumber(right.year || right.season) - asNumber(left.year || left.season),
    );
    return rows;
  }, [athlete?.achievements]);

  const latestSeason = seasonStats[0] || null;
  const priorSeasons = seasonStats.slice(1);
  const hasAnyPerformance =
    careers.length > 0 || seasonStats.length > 0 || positions.length > 0 || achievements.length > 0;

  if (athleteLoading) {
    return null;
  }

  if (!hasAnyPerformance) {
    return (
      <SectionCard title="Performance">
        <EmptyState
          description="Season stats, positions, career history, and achievements will show here once they are captured on the athlete profile."
          title="No performance records yet"
        />
      </SectionCard>
    );
  }

  return (
    <SchoolPlayerProfileTabStack>
      <SchoolPlayerProfilePerformancePrimaryGrid>
        <SectionCard title="Season output">
          {latestSeason ? (
            <SchoolPlayerProfilePerformanceList>
              <SchoolPlayerProfilePerformanceSnapshot>
                <SchoolPlayerProfilePerformanceSeasonHeader>
                  <div>
                    <SchoolPlayerProfileEyebrowText>Latest season</SchoolPlayerProfileEyebrowText>
                    <SchoolPlayerProfilePerformanceSnapshotTitle>
                      {latestSeason.season || "Unknown season"}
                    </SchoolPlayerProfilePerformanceSnapshotTitle>
                    <SchoolPlayerProfilePerformanceSeasonMeta>
                      {latestSeason.tournament || "Tournament not set"}
                    </SchoolPlayerProfilePerformanceSeasonMeta>
                  </div>
                </SchoolPlayerProfilePerformanceSeasonHeader>

                <SchoolPlayerProfilePerformanceMetricGrid>
                  <SchoolPlayerProfilePerformanceMetricTile>
                    <SchoolPlayerProfileEyebrowText>Matches</SchoolPlayerProfileEyebrowText>
                    <SchoolPlayerProfilePerformanceMetricValue>
                      {latestSeason.matches_played ?? 0}
                    </SchoolPlayerProfilePerformanceMetricValue>
                  </SchoolPlayerProfilePerformanceMetricTile>
                  <SchoolPlayerProfilePerformanceMetricTile>
                    <SchoolPlayerProfileEyebrowText>Points</SchoolPlayerProfileEyebrowText>
                    <SchoolPlayerProfilePerformanceMetricValue>
                      {latestSeason.points ?? 0}
                    </SchoolPlayerProfilePerformanceMetricValue>
                  </SchoolPlayerProfilePerformanceMetricTile>
                </SchoolPlayerProfilePerformanceMetricGrid>
              </SchoolPlayerProfilePerformanceSnapshot>

              {priorSeasons.length > 0
                ? priorSeasons.map((row: any, index: number) => (
                    <SchoolPlayerProfilePerformanceSeasonRow
                      key={String(row.id || `${row.season}-${row.tournament}-${index}`)}
                    >
                      <div className="min-w-0">
                        <SchoolPlayerProfilePerformanceSeasonTitle>
                          {row.season || "Unknown season"}
                        </SchoolPlayerProfilePerformanceSeasonTitle>
                        <SchoolPlayerProfilePerformanceSeasonMeta>
                          {row.tournament || "Tournament not set"}
                        </SchoolPlayerProfilePerformanceSeasonMeta>
                      </div>
                      <p className="shrink-0 text-right text-xs text-[var(--brand-muted)]">
                        {row.matches_played ?? 0} matches · {row.points ?? 0} pts
                      </p>
                    </SchoolPlayerProfilePerformanceSeasonRow>
                  ))
                : null}
            </SchoolPlayerProfilePerformanceList>
          ) : (
            <SchoolPlayerProfileEmptyCopy>No season stats recorded yet.</SchoolPlayerProfileEmptyCopy>
          )}
        </SectionCard>

        <SectionCard title="Positions">
          {positions.length > 0 ? (
            <SchoolPlayerProfilePerformanceList>
              {positions.map((row: any, index: number) => {
                const share = Math.max(0, Math.min(100, asNumber(row.percentage)));
                const appearances = asNumber(row.appearances);

                return (
                  <SchoolPlayerProfilePerformancePositionRow
                    key={String(row.id || `${row.position}-${row.season}-${index}`)}
                  >
                    <SchoolPlayerProfilePerformancePositionHeader>
                      <SchoolPlayerProfilePerformancePositionName>
                        {row.position || "Unknown"}
                      </SchoolPlayerProfilePerformancePositionName>
                      <SchoolPlayerProfilePerformancePositionMeta>
                        {share}% · {appearanceLabel(appearances)}
                        {row.season ? ` · ${row.season}` : ""}
                      </SchoolPlayerProfilePerformancePositionMeta>
                    </SchoolPlayerProfilePerformancePositionHeader>
                    <SchoolPlayerProfilePerformanceBarTrack>
                      <SchoolPlayerProfilePerformanceBarFill style={{ width: `${share}%` }} />
                    </SchoolPlayerProfilePerformanceBarTrack>
                  </SchoolPlayerProfilePerformancePositionRow>
                );
              })}
            </SchoolPlayerProfilePerformanceList>
          ) : (
            <SchoolPlayerProfileEmptyCopy>No position splits recorded yet.</SchoolPlayerProfileEmptyCopy>
          )}
        </SectionCard>
      </SchoolPlayerProfilePerformancePrimaryGrid>

      <SchoolPlayerProfilePerformanceSecondaryGrid>
        <SectionCard title="Career">
          {careers.length > 0 ? (
            <SchoolPlayerProfilePerformanceList>
              {careers.map((row: any, index: number) => (
                <SchoolPlayerProfilePerformanceCareerRow
                  key={String(row.id || `${row.team_name}-${row.start_year}-${index}`)}
                >
                  <SchoolPlayerProfilePerformanceCareerYear>
                    {row.start_year || "—"}
                  </SchoolPlayerProfilePerformanceCareerYear>
                  <div className="min-w-0">
                    <SchoolPlayerProfilePerformanceCareerTeam>
                      {row.team_name || "Unknown team"}
                    </SchoolPlayerProfilePerformanceCareerTeam>
                    <SchoolPlayerProfilePerformanceCareerMeta>
                      {[row.league_name, row.country_name].filter(Boolean).join(" · ") ||
                        "League not set"}
                    </SchoolPlayerProfilePerformanceCareerMeta>
                  </div>
                </SchoolPlayerProfilePerformanceCareerRow>
              ))}
            </SchoolPlayerProfilePerformanceList>
          ) : (
            <SchoolPlayerProfileEmptyCopy>No career history recorded yet.</SchoolPlayerProfileEmptyCopy>
          )}
        </SectionCard>

        <SectionCard title="Achievements">
          {achievements.length > 0 ? (
            <SchoolPlayerProfilePerformanceList>
              {achievements.map((row: any, index: number) => (
                <SchoolPlayerProfilePerformanceAchievementRow
                  key={String(row.id || `${row.title}-${row.season || row.year}-${index}`)}
                >
                  <SchoolPlayerProfilePerformanceAchievementTitle>
                    {row.title || "Untitled honour"}
                  </SchoolPlayerProfilePerformanceAchievementTitle>
                  <SchoolPlayerProfilePerformanceAchievementMeta>
                    {[row.season || row.year, row.tournament, row.level]
                      .filter(Boolean)
                      .join(" · ") || "Details not set"}
                  </SchoolPlayerProfilePerformanceAchievementMeta>
                </SchoolPlayerProfilePerformanceAchievementRow>
              ))}
            </SchoolPlayerProfilePerformanceList>
          ) : (
            <SchoolPlayerProfileEmptyCopy>No achievements recorded yet.</SchoolPlayerProfileEmptyCopy>
          )}
        </SectionCard>
      </SchoolPlayerProfilePerformanceSecondaryGrid>
    </SchoolPlayerProfileTabStack>
  );
};
